/**
 * Local news for the ISR workspace — conversation triggers from the lead's
 * city/region via the Webz.io News API Lite, with a SHARED Appwrite cache.
 *
 * Quota model (1,000 Webz.io calls/month):
 * - Cache documents in Appwrite (`local_news_cache` collection) live for
 *   24h per place. Every lead in the same city reads the SAME document —
 *   one API call serves the whole team for a day.
 * - The API is only hit when a cache miss occurs AND the caller explicitly
 *   asks for a refresh (the News tab). Passive surfaces (the footer ticker)
 *   read the cache only and never spend quota.
 */

import { Client, Databases, Query } from 'appwrite';

const WEBZ_URL = 'https://api.webz.io/newsApiLite';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// The news cache table lives in the `brain` database (created via console:
// /console/project-default…/databases/database-693c1ae3003d56af4154/table-local_news_cache).
const DATABASE_ID = '693c1ae3003d56af4154';
const COLLECTION_ID = 'local_news_cache';

export interface LocalNewsItem {
  title: string;
  url: string;
  source?: string;
  published?: string;
  /** Short derived summary — the Lite API ships no article text, so this is
   *  extracted from the headline itself ("Source: actual headline" → the
   *  part after the colon) for ticker/list context. */
  summary?: string;
}

/**
 * Derive a short summary from a headline. News titles often follow
 * "Source/Topic: the actual story" or "Story — detail" shapes; taking the
 * tail yields a cleaner sentence than the raw packed title. Falls back to a
 * trimmed title.
 */
const summarizeTitle = (title: string): string => {
  const cleaned = title.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';

  // "Economía.- USCA insta a…" → "USCA insta a…"
  const dotDash = cleaned.split(/\.\-|\s[—–-]\s/).map((part) => part.trim()).filter(Boolean);
  // Prefer the longest segment — usually the story, not the section label.
  const best = dotDash.sort((a, b) => b.length - a.length)[0] || cleaned;

  // "La gasolina más barata: así está hoy" — if a colon exists and the tail
  // is substantial, use it.
  const colonParts = best.split(/:\s+/);
  if (colonParts.length > 1 && colonParts[colonParts.length - 1].length > 24) {
    return colonParts.slice(1).join(': ').slice(0, 140);
  }

  return best.length > 140 ? `${best.slice(0, 137)}…` : best;
};

const client = new Client()
  .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
  .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '');
const databases = new Databases(client);

const token = (): string => import.meta.env.VITE_WEBZIO_TOKEN || '';

/** True when the service is configured (feature stays hidden otherwise). */
export const isLocalNewsEnabled = (): boolean => Boolean(token());

const placeKey = (place: string): string =>
  place.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120);

interface CacheDoc {
  items: LocalNewsItem[];
  fetchedAt: Date;
}

/** Read a fresh (<24h) cache document for the place, or null. */
const readCache = async (place: string): Promise<CacheDoc | null> => {
  try {
    const list = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
      Query.equal('place', placeKey(place)),
      Query.limit(1),
    ]);
    const doc = list.documents[0] as any;
    if (!doc) return null;
    const fetchedAt = new Date(doc.fetched_at);
    if (Date.now() - fetchedAt.getTime() > CACHE_TTL_MS) return null;
    const items = JSON.parse(doc.items_json || '[]') as LocalNewsItem[];
    return Array.isArray(items) && items.length ? { items, fetchedAt } : null;
  } catch {
    return null;
  }
};

const writeCache = async (place: string, items: LocalNewsItem[]): Promise<void> => {
  try {
    const key = placeKey(place);
    // Upsert by unique place key: replace stale content if present.
    const list = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
      Query.equal('place', key),
      Query.limit(1),
    ]);
    const payload = {
      place: key,
      items_json: JSON.stringify(items),
      fetched_at: new Date().toISOString(),
    };
    if (list.documents[0]) {
      await databases.updateDocument(DATABASE_ID, COLLECTION_ID, list.documents[0].$id, payload);
    } else {
      await databases.createDocument(DATABASE_ID, COLLECTION_ID, key, payload);
    }
  } catch {
    // Cache write failures are non-fatal — the caller still gets its items.
  }
};

/**
 * Cached news for a place. NEVER calls the Webz.io API — returns whatever
 * the shared cache has (or null). Safe for passive surfaces (ticker).
 */
export const readLocalNews = async (place: string): Promise<LocalNewsItem[] | null> => {
  if (!place.trim() || !isLocalNewsEnabled()) return null;
  const cached = await readCache(place);
  return cached?.items ?? null;
};

/**
 * Sports-noise filter — headlines about a CITY are dominated by its football
 * club, and the API's topic:business happily returns transfer news. Filter
 * by title/source vocabulary (multilingual, covers ES/EN football terms).
 */
const SPORTS_PATTERN = new RegExp(
  [
    'transfer', 'transfermarkt', 'goal\\.com', 'marca\\.com', 'mundodeportivo', 'sport\\b\\.es',
    'match', 'fixture', 'la ?liga', 'laliga', 'champions', 'europa league', 'copa del rey',
    'coach', 'manager.*(sack|appoint|hir)', 'striker', 'defender', 'midfielder', 'goalkeeper',
    'winger', 'kick', 'squad', 'line-?up', 'f\\.?c\\.?\\s', 'futbol', 'fútbol', 'football',
    'soccer', '\\bnba\\b', '\\bf1\\b', 'formula 1', 'tennis', 'player ratings',
    'bid for', 'signs? for', 'agrees? .*sale', 'preseason', 'pretemporada', 'amistoso',
    'partido', 'gol\\b', 'goles\\b', 'derbi', 'clásico', 'clasico', 'rodrygo', 'yamal',
    'laporta', 'de jong', 'lewandowski', 'raphinha', 'kounde', 'cubarsí', 'ter stegen',
  ].join('|'),
  'i',
);

const isSportsHeadline = (title: string, source?: string): boolean =>
  SPORTS_PATTERN.test(title) || (source ? SPORTS_PATTERN.test(source) : false);

/**
 * Fetch headlines for a place (user-initiated — the News tab). Queries the
 * CITY only (a "City, Region" quoted phrase matches nothing), restricts to
 * business-relevant topics, and strips residual sports noise client-side.
 * Reads the shared cache first; one Webz.io call per place per 24h.
 */
export const fetchLocalNews = async (place: string): Promise<LocalNewsItem[] | null> => {
  const key = place.trim();
  if (!key || !token()) return null;

  const cached = await readCache(key);
  if (cached) return cached.items;

  try {
    // The place arrives as "City, Region[, Country]" — quote only the city.
    const city = key.split(',')[0].trim();
    if (!city) return null;

    const query = encodeURIComponent(
      `"${city}" (topic:business OR topic:economy OR topic:finance OR topic:politics OR topic:tech)`,
    );
    const response = await fetch(`${WEBZ_URL}?token=${token()}&q=${query}&size=25`);
    if (!response.ok) return null;

    const json = await response.json();
    const posts: any[] = Array.isArray(json?.posts) ? json.posts : [];

    const items: LocalNewsItem[] = posts
      .map((post) => {
        const title = typeof post?.title === 'string' ? post.title.trim() : '';
        const url = typeof post?.url === 'string' ? post.url : '';
        if (!title || !url) return null;
        const source = typeof post?.thread?.site === 'string' ? post.thread.site : undefined;
        if (isSportsHeadline(title, source)) return null;
        return {
          title,
          url,
          source,
          summary: summarizeTitle(title),
          published: typeof post?.published === 'string' ? post.published : undefined,
        } as LocalNewsItem;
      })
      .filter((item): item is LocalNewsItem => item !== null)
      .slice(0, 8);

    if (items.length) {
      await writeCache(key, items);
      return items;
    }
    return null;
  } catch {
    return null;
  }
};
